// /lib/GTMEvents.ts
import {GTMManager} from "./GTMManager";

export type EcommerceItem = {
    item_id?: string;
    item_name?: string;
    price?: number;
    quantity?: number;
    [key: string]: any;
};
export type EcommerceCategory = {
    category_id?: string;
    category_name?: string;
    [key: string]: any;
};

/** Normalize legacy field names (itemId, name) to GA4 standard (item_id, item_name). */
export function normalizeEcommerceItem(item: EcommerceItem): EcommerceItem {
    return {
        ...item,
        item_id: item.item_id ?? item.itemId ?? item.id,
        item_name: item.item_name ?? item.itemName ?? item.name,
    };
}

function normalizeEcommerceItems(items: EcommerceItem | EcommerceItem[]): EcommerceItem[] {
    const itemsArr = Array.isArray(items) ? items : [items];
    return itemsArr.map(normalizeEcommerceItem);
}

/**
 * Builds the ecommerce payload for a cart line (the former web cart's CartItem shape; the planner storefront still uses it).
 *
 * This helper exists because of a shipped bug: cart lines carry TWO ids - `itemId`
 * (the product's real uuid) and `id` (a synthetic per-line key,
 * `${itemId}-${Date.now()}-${Math.random()}`, ~69 chars). Two call sites passed the
 * synthetic key as item_id, which overflowed the backend's entity_id column and
 * silently dropped the event. Route every cart-line payload through here so the id
 * selection is decided exactly once.
 */
export function cartLineToEcommerceItem(line: {
    itemId: string;
    name: string;
    price: number;
    vendorId?: string;
    vendorName?: string;
    quantity?: number;
}): EcommerceItem {
    return {
        item_id: line.itemId,
        item_name: line.name,
        price: line.price,
        quantity: line.quantity,
        vendor_id: line.vendorId,
        vendor_name: line.vendorName,
    };
}

export const GTMEvents = {
    viewItem: (items: EcommerceItem | EcommerceItem[]) => {
        const itemsArr = normalizeEcommerceItems(items);
        GTMManager.getInstance().pushEvent({
            event: "view_item",
            ecommerce: {
                items: itemsArr,
            },
        });
    },

    clickedItem: (items: EcommerceItem | EcommerceItem[]) => {
        const itemsArr = normalizeEcommerceItems(items);
        GTMManager.getInstance().pushEvent({
            event: "clicked_item",
            ecommerce: {
                items: itemsArr,
            },
        });
    },

    clickedItemAddons: (items: any) => {
        const itemsArr = Array.isArray(items) ? items : [items];
        GTMManager.getInstance().pushEvent({
            event: "clicked_item_addons",
            ecommerce: {
                items: itemsArr,
            },
        });
    },

    viewCategory: (cat: any) => {
        GTMManager.getInstance().pushEvent({
            event: "view_category",
            ecommerce: {
                category: cat,
            },
        });
    },

    addToCart: (items: EcommerceItem | EcommerceItem[], currency?: string, value?: number) => {
        const itemsArr = normalizeEcommerceItems(items);
        const eventObj: any = {
            event: "add_to_cart",
            ecommerce: {
                items: itemsArr,
            },
        };
        if (currency) eventObj.ecommerce.currency = currency;
        if (typeof value === "number") eventObj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(eventObj);
    },

    addToCartAi: (items: EcommerceItem | EcommerceItem[], currency?: string, value?: number) => {
        const itemsArr = normalizeEcommerceItems(items);
        const eventObj: any = {
            event: "add_to_cart_ai",
            ecommerce: {
                items: itemsArr,
            },
        };
        if (currency) eventObj.ecommerce.currency = currency;
        if (typeof value === "number") eventObj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(eventObj);
    },

    removeFromCart: (items: EcommerceItem | EcommerceItem[]) => {
        const itemsArr = normalizeEcommerceItems(items);
        const eventObj: any = {
            event: "remove_from_cart",
            ecommerce: {
                items: itemsArr,
            },
        };
        GTMManager.getInstance().pushEvent(eventObj);
    },

    beginCheckout: (items: EcommerceItem[], currency?: string, value?: number) => {
        const eventObj: any = {
            event: "begin_checkout",
            ecommerce: {
                items: normalizeEcommerceItems(items),
            },
        };
        if (currency) eventObj.ecommerce.currency = currency;
        if (typeof value === "number") eventObj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(eventObj);
    },

    addPaymentInfo: (items: EcommerceItem[], method: string, currency?: string, value?: number) => {
        const eventObj: any = {
            event: "add_payment_info",
            method: method,
            ecommerce: {
                items: normalizeEcommerceItems(items),
            },
        };
        if (currency) eventObj.ecommerce.currency = currency;
        if (typeof value === "number") eventObj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(eventObj);
    },

    purchase: (items: EcommerceItem[], transaction_id: any, currency: string, value: number, hasItems: boolean = false, hasVenues: boolean = false) => {
        const ecommerce: any = {
            currency,
            transaction_id,
            value,
            items: normalizeEcommerceItems(items),
            hasItems,
            hasVenues,
        };

        GTMManager.getInstance().pushEvent({
            event: "purchase",
            ecommerce,
        });
    },

    viewItemList: (items: EcommerceItem[], currency?: string, value?: number) => {
        const obj: any = {
            event: "view_item_list",
            ecommerce: {items: normalizeEcommerceItems(items)}
        };
        if (currency) obj.ecommerce.currency = currency;
        if (typeof value === "number") obj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(obj);
    },

    selectItem: (items: EcommerceItem[], currency?: string, value?: number) => {
        const obj: any = {
            event: "select_item",
            ecommerce: {items: normalizeEcommerceItems(items)}
        };
        if (currency) obj.ecommerce.currency = currency;
        if (typeof value === "number") obj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(obj);
    },

    viewCart: (items: EcommerceItem[], currency?: string, value?: number) => {
        const obj: any = {
            event: "view_cart",
            ecommerce: {items: normalizeEcommerceItems(items)}
        };
        if (currency) obj.ecommerce.currency = currency;
        if (typeof value === "number") obj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(obj);
    },

    addToWishlist: (items: EcommerceItem | EcommerceItem[], currency?: string, value?: number) => {
        const itemsArr = normalizeEcommerceItems(items);
        const eventObj: any = {
            event: "add_to_wishlist",
            ecommerce: {
                items: itemsArr,
            },
        };
        if (currency) eventObj.ecommerce.currency = currency;
        if (typeof value === "number") eventObj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(eventObj);
    },

    removeFromWishlist: (items: EcommerceItem | EcommerceItem[], currency?: string, value?: number) => {
        const itemsArr = normalizeEcommerceItems(items);
        const eventObj: any = {
            event: "remove_from_wishlist",
            ecommerce: {
                items: itemsArr,
            },
        };
        if (currency) eventObj.ecommerce.currency = currency;
        if (typeof value === "number") eventObj.ecommerce.value = value;
        GTMManager.getInstance().pushEvent(eventObj);
    },

    search: (searchTerm: string, itemsFound?: number) => {
        const eventObj: any = {
            event: "search",
            search_term: searchTerm,
        };
        if (typeof itemsFound === "number") eventObj.items_found = itemsFound;
        GTMManager.getInstance().pushEvent(eventObj);
    },

    filterApplied: (filterName: string, filterValue: string) => {
        GTMManager.getInstance().pushEvent({
            event: "filter_applied",
            filter_name: filterName,
            filter_value: filterValue,
        });
    },

    pageView: (pageTitle: string, pageLocation: string) => {
        GTMManager.getInstance().pushEvent({
            event: "page_view",
            page_title: pageTitle,
            page_location: pageLocation,
        });
    },

    authRegister: (dto: any) => {
        GTMManager.getInstance().pushEvent({
            event: "registration_start",
            payload: dto
        });
    },

    verifyOTP: (dto: any) => {
        GTMManager.getInstance().pushEvent({
            event: "registration_form_complete",
            payload: dto
        });
    },

    authCompleteRegister: (dto: any) => {
        GTMManager.getInstance().pushEvent({
            event: "user_registered",
            payload: dto
        });
    },

    // ---------------------------------------------------------------------
    // Funnel events (homepage / category / table reservation)
    // ---------------------------------------------------------------------

    /** Fires when the homepage is viewed. Top of the homepage->purchase funnel. */
    homepageView: (extra?: Record<string, any>) => {
        GTMManager.getInstance().pushEvent({
            event: "homepage_view",
            page_type: "homepage",
            ...(extra || {}),
        });
    },

    /**
     * Fires when a single category page is viewed.
     * Captures category_id / category_name for "most viewed category" reporting.
     */
    categoryView: (category: EcommerceCategory) => {
        GTMManager.getInstance().pushEvent({
            event: "category_view",
            category_id: category.category_id,
            category_name: category.category_name,
            ...category,
        });
    },

    /**
     * Subcategory detail page view. Carries both languages so the admin event log and
     * its free-text search work regardless of the locale the customer was browsing in,
     * plus the parent category for roll-up reporting.
     */
    subcategoryView: (payload: {
        subcategory_id: string
        subcategory_name_en?: string
        subcategory_name_ar?: string
        category_id?: string
        category_name_en?: string
        category_name_ar?: string
    }) => {
        GTMManager.getInstance().pushEvent({
            event: "subcategory_view",
            ...payload,
        });
    },

    /**
     * Fires when a list of categories is rendered (e.g. categories hub / homepage
     * sections) to measure category impressions for "most viewed category".
     */
    categoryImpressions: (categories: EcommerceCategory[], listName?: string) => {
        GTMManager.getInstance().pushEvent({
            event: "category_impression",
            item_list_name: listName || "categories",
            categories: (categories || []).map((c) => ({
                category_id: c.category_id,
                category_name: c.category_name,
            })),
        });
    },

    /**
     * Restaurant view = vendor detail page.
     * Captures restaurant_id / restaurant_name for "most viewed restaurant".
     */
    restaurantView: (vendor: Record<string, any>, locale?: string) => {
        const name =
            (locale === "ar" ? vendor.name_ar : vendor.name_en) ||
            vendor.name_en ||
            vendor.name_ar ||
            vendor.name;
        GTMManager.getInstance().pushEvent({
            event: "restaurant_view",
            restaurant_id: vendor.restaurant_id ?? vendor.id,
            restaurant_name: vendor.restaurant_name ?? name,
            vendor_id: vendor.vendor_id ?? vendor.id,
            vendor_name: vendor.vendor_name ?? name,
        });
    },

    /** External restaurant view = external (in-venue) booking page. */
    externalRestaurantView: (vendor: Record<string, any>, locale?: string) => {
        const name =
            (locale === "ar" ? vendor.name_ar : vendor.name_en) ||
            vendor.name_en ||
            vendor.name_ar ||
            vendor.name;
        GTMManager.getInstance().pushEvent({
            event: "external_restaurant_view",
            restaurant_id: vendor.restaurant_id ?? vendor.id,
            restaurant_name: vendor.restaurant_name ?? name,
            vendor_id: vendor.vendor_id ?? vendor.id,
            vendor_name: vendor.vendor_name ?? name,
        });
    },

    /** Table reservation funnel — venue/reservation page viewed. */
    reservationPageView: (venue: Record<string, any>) => {
        GTMManager.getInstance().pushEvent({
            event: "reservation_page_view",
            venue_id: venue.venue_id ?? venue.id,
            venue_name: venue.venue_name ?? venue.title_en ?? venue.title_ar,
            // Both languages travel alongside the resolved name so the admin event log
            // stays searchable in English and Arabic (see eventTracker's venue_viewed).
            venue_name_en: venue.title_en,
            venue_name_ar: venue.title_ar,
            vendor_id: venue.vendor_id,
            vendor_name: venue.vendor_name,
            vendor_name_en: venue.vendor_name_en,
            vendor_name_ar: venue.vendor_name_ar,
            physical_space_id: venue.physical_space_id,
            physical_space_name: venue.physical_space_name,
            physical_space_name_en: venue.physical_space_name_en,
            physical_space_name_ar: venue.physical_space_name_ar,
        });
    },

    /** Table reservation funnel — user started the booking (opened the form). */
    reservationStarted: (venue: Record<string, any>) => {
        GTMManager.getInstance().pushEvent({
            event: "reservation_started",
            venue_id: venue.venue_id ?? venue.id,
            venue_name: venue.venue_name ?? venue.title_en ?? venue.title_ar,
            vendor_id: venue.vendor_id,
            vendor_name: venue.vendor_name,
        });
    },

    /**
     * Table reservation funnel — reservation details completed (booking added).
     * The actual revenue conversion is still the `purchase` event at checkout.
     */
    reservationCompleted: (payload: Record<string, any>) => {
        GTMManager.getInstance().pushEvent({
            event: "reservation_completed",
            venue_id: payload.venue_id ?? payload.venueId,
            venue_name: payload.venue_name ?? payload.venueName,
            vendor_id: payload.vendor_id ?? payload.vendorId,
            vendor_name: payload.vendor_name ?? payload.vendorName,
            physical_space_id: payload.physical_space_id ?? payload.physicalSpaceId,
            physical_space_name: payload.physical_space_name ?? payload.physicalSpaceName,
            value: payload.value ?? payload.calculatedPrice,
            currency: payload.currency || "SAR",
        });
    },

    customEvent: (eventName: string, eventData?: Record<string, any>) => {
        const eventObj: any = {
            event: eventName,
        };
        if (eventData && typeof eventData === "object" && !Array.isArray(eventData)) {
            Object.assign(eventObj, eventData);
        }
        GTMManager.getInstance().pushEvent(eventObj);
    },
};
